import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import FactionMpProvider from "@/core/data-provider/data-mp/faction/faction-mp-provider";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import toast from "@/domains/toasts/helpers/toast";
import { QUERY_KEY_FACTION_GENERAL_INFO } from "@/domains/faction/hooks/useGetInfo";

type KnownErr = Error & { knownI18nKey?: boolean };
type Vars = { memberId: number; cb?: () => void };

export function useDismissMember() {
  const { t, i18n } = useTranslation("faction");
  const queryClient = useQueryClient();

  return useMutation<void, KnownErr, Vars>({
    mutationFn: async ({ memberId }) => {
      const response = await FactionMpProvider.fireMember(memberId);

      if (response instanceof RpcMpProviderFailedResponse) {
        const messageKey = response?.error?.message ?? "Unknown";
        const knownI18nKey = i18n.exists(`faction:faction.errors.${messageKey}`);
        const error = new Error(messageKey) as KnownErr;
        error.knownI18nKey = knownI18nKey;
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      toast.success(t("faction.toast.dismissMemberSuccess"));
      variables?.cb?.();
      queryClient.refetchQueries({ queryKey: [QUERY_KEY_FACTION_GENERAL_INFO] });
    },
    onError: (err) => {
      const error = err as KnownErr;
      console.error(error);
      if (error.knownI18nKey) {
        toast.error(t(`faction.errors.${error.message}`));
      } else {
        toast.error(t("faction.toast.dismissMemberError"));
      }
    },
  });
}
